// Count clicks on a few calls to action. Only the allow-listed button name is sent, to this
// site's own /api/event endpoint: no cookies, identifiers, page text, or URLs. The privacy
// notice describes this. Keep these names in sync with eventNames in worker/index.mjs.
export const ctaEventNames = [
  'assessment_request',
  'book_call',
  'capability_statement',
  'discuss_project',
] as const;

export type CtaEventName = (typeof ctaEventNames)[number];

const isCtaEventName = (value: string | undefined): value is CtaEventName =>
  ctaEventNames.includes(value as CtaEventName);

export function sendCtaEvent(name: CtaEventName) {
  try {
    const body = JSON.stringify({ name });
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon('/api/event', new Blob([body], { type: 'application/json' }));
    } else if (typeof fetch === 'function') {
      fetch('/api/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => undefined);
    }
  } catch {
    // Counting is best effort and never affects navigation.
  }
}

function onClick(event: MouseEvent) {
  const target = event.target instanceof Element ? event.target : null;
  const name = target?.closest<HTMLElement>('[data-track]')?.dataset.track;
  if (isCtaEventName(name)) sendCtaEvent(name);
}

/** Listen once at the document level; returns a function that stops listening. */
export function startCtaCounting(doc: Document = document) {
  doc.addEventListener('click', onClick, { capture: true });
  return () => doc.removeEventListener('click', onClick, { capture: true });
}
